import moment from 'moment';
import { rgba } from 'polished';
import React, { useMemo } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useWorkspace } from '~/contexts';
import { useAuth } from '~/hooks';
import { colors, weights } from '~/styles';

const urgencyStyles = {
  success: css`
    background-color: ${rgba(colors.success, 0.15)};
    border-color: ${rgba(colors.success, 0.3)};

    &:hover {
      background-color: ${rgba(colors.success, 0.25)};
    }
  `,
  warning: css`
    background-color: ${rgba(colors.warning, 0.2)};
    border-color: ${rgba(colors.warning, 0.4)};

    &:hover {
      background-color: ${rgba(colors.warning, 0.3)};
    }
  `,
  danger: css`
    background-color: ${rgba(colors.danger, 0.25)};
    border-color: ${rgba(colors.danger, 0.4)};

    &:hover {
      background-color: ${rgba(colors.danger, 0.35)};
    }
  `,
};

const Card = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background-color: ${rgba(colors.white, 0.1)};
  border: solid 0.0625rem ${rgba(colors.white, 0.2)};
  border-radius: 0.375rem;
  width: 9.0625rem;
  text-decoration: none;

  &:hover {
    background-color: ${rgba(colors.white, 0.15)};
  }

  ${({ $urgency }) => $urgency && urgencyStyles[$urgency]}
`;

const CardText = styled.p`
  margin: 0;
  color: ${colors.white};
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  line-height: 1.4;
`;

const HoverLabel = styled.div`
  position: absolute;
  top: 50%;
  left: calc(100% - 0.125rem);
  padding: 0.5rem;
  background-color: ${colors.white};
  border-radius: 0.25rem;
  box-shadow: 0 0 0.625rem ${rgba(colors.black, 0.25)};
  color: ${colors.black};
  font-size: 0.75rem;
  font-weight: normal;
  line-height: 0.875rem;
  width: max-content;
  max-width: 14rem;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0;
  z-index: 95;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -0.375rem;
    width: 0.375rem;
    height: 0.75rem;
    transform: translateY(-50%);
    background-color: ${colors.white};
    clip-path: polygon(100% 0, 100% 100%, 0 50%);
  }
`;

const BadgeWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;

  &:hover ${HoverLabel} {
    opacity: 1;
  }
`;

const Badge = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background-color: ${rgba(colors.white, 0.1)};
  border: solid 0.0625rem ${rgba(colors.white, 0.2)};
  border-radius: 0.375rem;
  text-decoration: none;

  &:hover {
    background-color: ${rgba(colors.white, 0.15)};
  }

  ${({ $urgency }) => $urgency && urgencyStyles[$urgency]}
`;

const BadgeText = styled.span`
  color: ${colors.white};
  font-size: 0.875rem;
  font-weight: ${weights.bold};
`;

const BadgeDot = styled.div`
  position: absolute;
  top: -0.1875rem;
  right: -0.1875rem;
  width: 0.625rem;
  height: 0.625rem;
  background-color: ${({ $color }) => $color || colors.danger};
  border-radius: 50%;
`;

export default function SidebarTrialWidget({ collapsed }) {
  const match = useRouteMatch();
  const auth = useAuth();
  const { workspace } = useWorkspace();

  const daysLeft = useMemo(() => {
    const trialEndsAt = workspace?.billingStatus?.trialEndsAt;
    if (!trialEndsAt) return null;
    const days = Math.ceil(moment(trialEndsAt).diff(moment(), 'days', true));
    return days > 0 ? days : null;
  }, [workspace?.billingStatus?.trialEndsAt]);

  const isConfigured = workspace?.billingStatus?.isConfigured;

  if (daysLeft == null || !auth.workspace.manage) return null;

  // Only show urgency coloring when billing is not configured
  const urgency = isConfigured ? null : daysLeft <= 5 ? 'danger' : daysLeft <= 14 ? 'warning' : 'success';
  const dotColor = isConfigured
    ? null
    : daysLeft <= 5
      ? colors.danger
      : daysLeft <= 14
        ? colors.warning
        : colors.success;

  if (collapsed) {
    return (
      <BadgeWrapper>
        <Badge to={`${match.url}/settings/billing`} $urgency={urgency}>
          <BadgeText>{daysLeft}</BadgeText>
          {dotColor && <BadgeDot $color={dotColor} />}
        </Badge>
        <HoverLabel>{daysLeft} days left on your free trial.</HoverLabel>
      </BadgeWrapper>
    );
  }

  return (
    <Card to={`${match.url}/settings/billing`} $urgency={urgency}>
      <CardText>{daysLeft} days left on your free trial.</CardText>
    </Card>
  );
}
